import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import * as EmailValidator from 'email-validator';

import {
  getCustomerById,
  uploadFiles,
  deleteCustomer,
  updateCustomer,
  getCustomerHistory,
  getCustomerReminders,
  getCustomerDocuments,
} from 'api/customers';
import { getAllDoctors } from 'api/doctors';
import { createCustomerDoctors } from 'api/customer_doctors';

import { serialize } from 'components/text_editor/text_editor_config';

import DocumentUploadModal from 'components/upload/modal';
import ReminderModal from 'components/reminder/modal';
import CustomerHeaderCard from 'components/cards/customer_header';

import Modal from 'components/modal/delete';
import { Tabz, TabPane } from 'components/tabz/tabz';
import GeneralInfo from './view_component/general_info/admin';
import CustomerHistory from './view_component/history/admin';
import CustomerDocuments from './view_component/documents/admin';
import CustomerReminders from './view_component/reminders/admin';

import NoAccess from 'pages/no_access';

import Loading from 'components/loading/loading';
import Deleted from 'components/deleted/deleted';
import Dropdown from 'components/dropdown/dropdown';

import { sanitize } from 'helpers/customer';
import { isPhoneValid } from 'helpers/phone';
import { calculateBMI } from 'helpers/bmi';
import { SCHEDULE_DROPDOWN_OPTIONS } from 'config/customer';

import { ReactComponent as ArrowRightSVG } from 'icons/arrow-right.svg';

const AdminCustomerItem = ({ onMessages }) => {
  const { id } = useParams();
  const history = useHistory();

  const currentTab = history.location.search?.replace('?tab=', '') || 'history';

  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [customerDocuments, setCustomerDocumnets] = useState([]);
  const [deletedByData, setDeletedByData] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [customer, setCustomer] = useState();
  const [originalCustomer, setOriginalCustomer] = useState();
  const [customerDoctors, setCustomerDoctors] = useState();
  const [doctorOptions, setDoctorOptions] = useState();
  const [customerHistory, setCustomerHistory] = useState(null);
  const [customerReminders, setCustomerReminders] = useState(null);
  const [fileUploadModalOpen, setFileUploadModalOpen] = useState(false);
  const [fileUploadProgress, setFileUploadProgress] = useState(0);
  const [fileUploadProgressLabel, setFileUploadProgressLabel] = useState('');
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [validation, setValidation] = useState({
    email: true,
    carteVitale: true,
    phone: true,
    height: true,
    weight: true,
    bmi: true,
  });

  const fetchCustomer = useCallback(async () => {
    setLoading(true);
    const data = await getCustomerById(id);

    if (data.deletedAt && data.deletedBy) {
      setDeletedByData({
        email: data.deletedBy.email,
        deletedAt: data.deletedAt,
      });
    } else {
      const customerDoctors = data.customerDoctors
        ?.map((d) => ({
          id: d.doctor.id,
          value: d.doctor.id,
          doctorId: d.doctor.id,
          customerId: Number(id),
          label: `${d.doctor.lastName?.toUpperCase()} ${d.doctor.firstName}`,
        }))
        .toSorted((a, b) => a.label.localeCompare(b.label));

      setCustomerDoctors(customerDoctors);
      setCustomer(data);
      setOriginalCustomer(data);
    }

    setLoading(false);
  }, [id]);

  const handleFileUpload = useCallback(
    async (type, files, isShared) => {
      const filesMap = files.map((f) => f.file);

      const {
        document,
        meta: { messages },
      } = await uploadFiles({
        id,
        files: filesMap,
        type: type?.id,
        isShared,
        path: 'customers',
        onUploadProgress: (e) => {
          const progress = Math.round((e.loaded / e.total) * 100);
          setFileUploadProgress(progress);

          progress < 100 &&
            setFileUploadProgressLabel(
              '<h3 class="midgrey-d1 f2">Uploading...</h3>',
            );

          progress === 100 &&
            setFileUploadProgressLabel(
              '<h3 class="midgrey-d1 f2">Processing...</h3>',
            );
        },
      });

      onMessages(messages);

      if (document) {
        setFileUploadModalOpen(false);
        await fetchCustomer();
      }
    },
    [id, onMessages],
  );

  const handleDelete = async () => {
    await deleteCustomer(id);
    history.push('/customers');

    setDeleteModalOpen(false);
  };

  const handleChange = (e) => {
    let { name, value } = e.target;

    if (name === 'height') {
      setCustomer({
        ...customer,
        height: value,
        bmi: calculateBMI(value, customer.weight),
      });

      return;
    }

    if (name === 'weight') {
      setCustomer({
        ...customer,
        weight: value,
        bmi: calculateBMI(customer.height, value),
      });

      return;
    }

    if (name === 'carteVitale') {
      setCustomer({
        ...customer,
        carteVitale: value.toUpperCase(),
      });

      return;
    }

    setCustomer({
      ...customer,
      [name]: value,
    });
  };

  const handleAddressSelect = (a) => {
    setCustomer((prevCustomer) => ({
      ...prevCustomer,
      address: a,
    }));
  };

  const handlePatientRightChange = (e) => {
    const { name, id } = e.target;

    setCustomer({
      ...customer,
      [name]: id,
    });
  };

  const handleGenderChange = (e) => {
    const { name, id } = e.target;

    setCustomer({
      ...customer,
      [name]: id,
    });
  };

  const handleDateChange = ({ value }) => {
    value.setHours(4, 0, 0, 0); // set to 4am to avoid timezone issues

    setCustomer({
      ...customer,
      dateOfBirth: value,
    });
  };

  const handlePhoneChange = (phone) => {
    setCustomer({
      ...customer,
      phone,
    });
  };

  const handleDoctorSelect = (selectedOptions) => {
    setCustomerDoctors(selectedOptions);
  };

  const checkValidity = () => {
    const formattedCarteVitale = customer.carteVitale?.replaceAll(' ', '');

    const validation = {
      email: EmailValidator.validate(customer.email?.trim()),
      carteVitale: customer.carteVitale
        ? /^\d{6}[0-9AB]\d{8}$/.test(formattedCarteVitale) &&
          formattedCarteVitale.length === 15
        : true,
      phone: customer.phone ? isPhoneValid(customer.phone) : true,
      height: customer.height
        ? /^\d*\.?\d*$/.test(customer.height?.trim())
        : true,
      weight: customer.weight
        ? /^\d*\.?\d*$/.test(customer.weight?.trim())
        : true,
    };

    setValidation(validation);

    return Object.values(validation).every(Boolean);
  };

  const handleSave = async () => {
    if (checkValidity()) {
      setLoading(true);

      const nurseNotes = Array.isArray(customer.nurseNotes)
        ? customer.nurseNotes?.map(serialize).join('')
        : customer.nurseNotes;

      const phone =
        customer.phone === '+33' || customer.phone === ''
          ? null
          : customer.phone;

      const sanitizedCustomer = sanitize(customer);

      await updateCustomer(id, {
        ...sanitizedCustomer,
        nurseNotes,
        phone,
      });

      await createCustomerDoctors(customerDoctors || [{ customerId: id }]);

      setCustomer({
        ...customer,
        nurseNotes,
        phone,
      });
      setOriginalCustomer({
        ...customer,
        nurseNotes,
        phone,
      });
      setLoading(false);
    } else {
      checkValidity();
    }
  };

  useEffect(() => {
    fetchCustomer();
  }, [fetchCustomer]);

  useEffect(() => {
    const fetchDoctors = async () => {
      const doctors = await getAllDoctors();

      const doctorOptions = doctors
        .map((d) => ({
          id: d.id,
          value: d.id,
          doctorId: d.id,
          customerId: Number(id),
          label: `${d.lastName?.toUpperCase()} ${d.firstName}`,
        }))
        .toSorted((a, b) => a.label.localeCompare(b.label));

      setDoctorOptions(doctorOptions);
    };

    fetchDoctors();
  }, []);

  useEffect(() => {
    const fetchCustomerHistory = async () => {
      setHistoryLoading(true);
      const customerHistory = await getCustomerHistory(id);
      setCustomerHistory(customerHistory);
      setHistoryLoading(false);
    };

    const fetchCustomerReminders = async () => {
      const { reminders } = await getCustomerReminders(id);
      setCustomerReminders(reminders);
    };

    const fetchCustomerDocumnets = async () => {
      setDocumentsLoading(true);
      const { documents: customerDocuments } = await getCustomerDocuments(id);
      setCustomerDocumnets(customerDocuments);
      setDocumentsLoading(false);
    };

    if (currentTab === 'history') {
      fetchCustomerHistory();
    }
    if (currentTab === 'documents') {
      fetchCustomerDocumnets();
    }

    if (currentTab === 'reminders') {
      fetchCustomerReminders();
    }
  }, [currentTab]);

  if (loading) return <Loading />;
  if (customer?.statusCode === 403) return <NoAccess />;
  if (deletedByData) return <Deleted deletedBy={deletedByData} />;

  return (
    <>
      <main className="th-main">
        <section className="th-canvas">
          <section className="view-container">
            <header className="th-canvas-header">
              <h2 className="th-canvas-title" data-test-screen-title>
                <Link to="/patients">Patients</Link>
                <span>
                  <ArrowRightSVG />
                </span>
                Patient profile
              </h2>
              <section className="view-actions mr4">
                <button
                  className="btn-white"
                  data-cy="reminder"
                  onClick={() => setReminderModalOpen(true)}
                >
                  <span>Set reminder</span>
                </button>
              </section>
              <section className="view-actions">
                <Dropdown
                  title="Schedule"
                  options={SCHEDULE_DROPDOWN_OPTIONS({
                    id: customer.id,
                    name: customer.firstName,
                    lastName: customer.lastName,
                    email: customer.email,
                    phone: customer.phone,
                    utm_source: 'admin',
                    utm_medium: 'webapp',
                  })}
                />
                <button
                  className="mr5 btn-white"
                  data-cy="upload"
                  onClick={() => setFileUploadModalOpen(true)}
                >
                  <span>Upload</span>
                </button>
                <button
                  className="btn-red"
                  data-cy="delete"
                  onClick={() => setDeleteModalOpen(true)}
                >
                  <span>Delete</span>
                </button>
              </section>
            </header>

            <CustomerHeaderCard data={originalCustomer} role="admin" />

            <Tabz loading={loading} currentTab={currentTab} onSave={handleSave}>
              <TabPane title="Details">
                <GeneralInfo
                  customer={customer}
                  customerDoctors={customerDoctors}
                  doctorOptions={doctorOptions}
                  validation={validation}
                  onChange={handleChange}
                  onAddressSelect={handleAddressSelect}
                  onDateChange={handleDateChange}
                  onPatientRightsChange={handlePatientRightChange}
                  onGenderChange={handleGenderChange}
                  onPhoneChange={handlePhoneChange}
                  onDoctorSelect={handleDoctorSelect}
                />
              </TabPane>
              <TabPane title="History">
                <CustomerHistory
                  loading={historyLoading}
                  history={customerHistory}
                />
              </TabPane>
              <TabPane title="Documents">
                <CustomerDocuments
                  loading={documentsLoading}
                  documents={customerDocuments}
                />
              </TabPane>
              <TabPane title="Reminders">
                <CustomerReminders reminders={customerReminders} />
              </TabPane>
            </Tabz>
          </section>
        </section>
      </main>
      {fileUploadModalOpen && (
        <DocumentUploadModal
          fileUploadProgress={fileUploadProgress}
          fileUploadProgressLabel={fileUploadProgressLabel}
          onClose={() => setFileUploadModalOpen(false)}
          onUpload={(type, files, isShared) =>
            handleFileUpload(type, files, isShared)
          }
          onClickOutside={() => setFileUploadModalOpen(false)}
        />
      )}
      {deleteModalOpen && (
        <Modal
          title="Customer delete"
          content={`Are you sure you want to delete the profile of <b>${
            customer && customer.firstName + ' ' + customer.lastName
          }</b>?`}
          onClickOutside={() => setDeleteModalOpen(false)}
          onClose={() => setDeleteModalOpen(false)}
          onDelete={() => handleDelete()}
        />
      )}
      {reminderModalOpen && (
        <ReminderModal
          onClose={() => setReminderModalOpen(false)}
          onClickOutside={() => setReminderModalOpen(false)}
          customerId={id}
        />
      )}
    </>
  );
};

export default AdminCustomerItem;
